import { getBodyTree } from "../../stores/sceneActions/bodyUtils";
import { addWorldCameras, addWorldSites, createBodyProcessor, layoutBodyRoots } from "./bodyLayout";
import {
  addActuators,
  addEqualities,
  addJoints,
  addTendons,
  buildTendonByName,
} from "./relationshipLayout";
import { applyParallelArcLanes, buildRootBodyLabels } from "./layoutFinalization";
import { buildLayoutContext } from "./layoutContext";

/**
 * Pure layout computation for the scene-graph diagram.
 *
 * Given the current scene state (bodies, joints, sites, meshes, cameras,
 * actuators, tendons, equalities) and the effective set of hidden types,
 * produce the nodes/edges/lookup maps that the renderer will draw.
 *
 * No React, no refs, no DOM - safe to call in any context (including tests).
 */
export function computeSceneGraphDiagramData({
  bodies,
  effectiveWorldBodies,
  joints,
  sites,
  meshes,
  cameras,
  actuators,
  tendons,
  equalities,
  effectiveHiddenTypes,
}) {
  const bodyTree = getBodyTree({ bodies, worldBodies: effectiveWorldBodies });
  const nodes = [];
  const edges = [];
  const idState = { nextId: 0 };

  // Use the effective (dependency-resolved) hidden set throughout layout so
  // that e.g. actuators are automatically excluded when joints are hidden.
  const hiddenTypes = effectiveHiddenTypes;
  const context = buildLayoutContext({
    bodyTree,
    bodies,
    effectiveWorldBodies,
    joints,
    cameras,
    actuators,
    tendons,
    effectiveHiddenTypes: hiddenTypes,
  });

  const processBody = createBodyProcessor({
    nodes,
    edges,
    idState,
    context,
    meshes,
    sites,
  });
  let rightmostRootEdge = layoutBodyRoots({ bodyTree, processBody, context });
  rightmostRootEdge = addWorldSites({
    nodes,
    idState,
    bodies,
    sites,
    context,
    rightmostRootEdge,
  });
  addWorldCameras({ nodes, idState, context, rightmostRootEdge });

  addJoints({ nodes, edges, idState, bodyTree, bodies, joints, context });

  const nodesByUuid = new Map(nodes.filter((node) => node.uuid).map((node) => [node.uuid, node]));
  const maxBodyY = nodes
    .filter((node) => node.type === "body")
    .reduce((maxY, node) => Math.max(maxY, node.y), 0);
  const globalY = maxBodyY + 180;
  const tendonByName = buildTendonByName(tendons);

  addTendons({
    nodes,
    edges,
    idState,
    bodies,
    tendons,
    actuators,
    nodesByUuid,
    tendonByName,
    hiddenTypes,
    globalY,
  });
  addActuators({
    nodes,
    edges,
    idState,
    actuators,
    joints,
    nodesByUuid,
    tendonByName,
    context,
    globalY,
  });
  addEqualities({
    nodes,
    edges,
    idState,
    equalities,
    nodesByUuid,
    hiddenTypes,
    globalY,
  });

  const rootBodyLabels = buildRootBodyLabels({
    bodyTree,
    nodes,
    hiddenTypes,
    rootBodyToGlobalName: context.rootBodyToGlobalName,
  });
  applyParallelArcLanes(edges);

  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  return { nodes, edges, nodeById, rootBodyLabels };
}
